import { useState } from "react";
import { AlertTriangle, Check, Copy, Info, Loader2 } from "lucide-react";
import { ModelResponse, ModelPricing } from "./types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../../lib/utils";
import { copyToClipboard } from "../../utils/clipboard";

interface ModelResponseCardProps {
    modelId: string;
    response: ModelResponse | undefined;
    pricing?: ModelPricing;
    hideModelName?: boolean;
}

export default function ModelResponseCard({ modelId, response, pricing, hideModelName = false }: ModelResponseCardProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (response?.content) {
            const ok = await copyToClipboard(response.content);
            if (ok) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        }
    };

    const formatLatency = (ms: number | null) => {
        if (ms === null) return '-';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    };

    const calculateCost = () => {
        if (!response?.tokenCount || !pricing) return null;
        const { input, output } = response.tokenCount;
        if (input === 0 && output === 0) return null;
        const inputCost = (input / 1_000_000) * pricing.input_price;
        const outputCost = (output / 1_000_000) * pricing.output_price;
        return inputCost + outputCost;
    };

    const formatCost = (cost: number | null) => {
        if (cost === null) return null;
        return `R$ ${cost.toFixed(12)}`;
    };

    const hasContent = response?.content && response.content.length > 0;
    const cost = calculateCost();
    const hasError = !!response?.error;

    let dotColor = "bg-muted-foreground";
    let dotPulse = false;
    if (response) {
        if (response.error) dotColor = "bg-destructive";
        else if (response.isStreaming) { dotColor = "bg-warning"; dotPulse = true; }
        else if (response.isComplete) dotColor = "bg-success";
    }

    return (
        <TooltipProvider>
            <div
                className={cn(
                    "flex max-h-[400px] min-h-[200px] flex-col overflow-hidden rounded-md border bg-card",
                    hasError ? "border-destructive" : "border-border"
                )}
            >
                {!hideModelName && (
                    <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2.5">
                        <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                            <span className={cn("h-2 w-2 rounded-full", dotColor, dotPulse && "animate-pulse")} />
                            {modelId}
                        </div>
                        {response?.isStreaming && (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-4 min-h-[100px]">
                    {!response && (
                        <p className="text-sm italic text-muted-foreground">Aguardando geração...</p>
                    )}
                    {response?.error && (
                        <div className="flex items-start gap-2 text-sm text-destructive">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{response.error}</span>
                        </div>
                    )}
                    {response && !response.error && (
                        <div className="whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-foreground">
                            {response.content || (response.isStreaming ? '' : 'Sem resposta')}
                            {response.isStreaming && (
                                <span className="ml-0.5 inline-block h-[1em] w-[2px] animate-pulse bg-primary align-text-bottom" />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-4 py-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        {response?.tokenCount && response.tokenCount.input > 0 && (
                            <div className="flex items-center gap-1 text-[11px]">
                                <span className="text-muted-foreground">Tokens input:</span>
                                <span className="font-semibold text-foreground">{response.tokenCount.input}</span>
                            </div>
                        )}
                        {response?.tokenCount && response.tokenCount.output > 0 && (
                            <div className="flex items-center gap-1 text-[11px]">
                                <span className="text-muted-foreground">Tokens output:</span>
                                <span className="font-semibold text-foreground">{response.tokenCount.output}</span>
                            </div>
                        )}
                        {cost !== null && (
                            <div className="flex items-center gap-1 text-[11px]">
                                <span className="text-muted-foreground">Custo:</span>
                                <span className="font-semibold text-foreground">{formatCost(cost)}</span>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info className="h-3 w-3 cursor-help text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>Custo calculado com preço de horário de pico (sem descontos)</TooltipContent>
                                </Tooltip>
                            </div>
                        )}
                        {response?.latencyMs !== null && response?.latencyMs !== undefined && (
                            <div className="flex items-center gap-1 text-[11px]">
                                <span className="text-muted-foreground">Tempo:</span>
                                <span className="font-semibold text-foreground">{formatLatency(response.latencyMs)}</span>
                            </div>
                        )}
                    </div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={handleCopy}
                                disabled={!hasContent}
                                aria-label={copied ? "Copiado!" : "Copiar resposta"}
                                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>{copied ? "Copiado!" : "Copiar resposta"}</TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </TooltipProvider>
    );
}
